// App.js
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import axios from 'axios';
import Sidebar from './Sidebar';
import ContentManager from './ContentManager';
import ChatInterface from './ChatInterface';
import './styles.css';
import LoginPage from './LoginPage';
import PopupChatbot from './PopupChatbot';
import { auth } from './firebase';
import ErrorBoundary from './ErrorBoundary';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState({ name: 'Home', id: 'home' });
  const [folders, setFolders] = useState({});

  // --- 1) Inject FirstPromoter scripts globally ---
  useEffect(() => {
    // Only inject once, no matter what page the user is on
    const scriptFPRInit = document.createElement('script');
    scriptFPRInit.innerHTML = `
      (function(w){
        w.fpr = w.fpr || function(){
          w.fpr.q = w.fpr.q || [];
          w.fpr.q[arguments[0]=='set' ? 'unshift' : 'push'](arguments);
        };
      })(window);
      // IMPORTANT: Replace with your campaign ID if different
      fpr("init", { cid: "qck7zrcm" }); 
      fpr("click");
    `;

    const scriptFPR = document.createElement('script');
    scriptFPR.src = "https://cdn.firstpromoter.com/fpr.js";
    scriptFPR.async = true;

    const scriptFPRLinks = document.createElement('script');
    scriptFPRLinks.innerHTML = `
      function getFPTid() {
        return window.FPROM && window.FPROM.data.tid;
      }
      function initializeFPRPaymentLinks() {
        console.log("initialized fpr on payment links");
        setTimeout(function () {
          var stripePaymentLinks = document.querySelectorAll(
            'a[href^="https://buy.stripe.com/"]'
          );
          stripePaymentLinks.forEach(function (link) {
            var oldStripePaymentUrl = link.getAttribute("href");
            var tid = getFPTid();
            if (tid) {
              var url = new URL(oldStripePaymentUrl);
              url.searchParams.set('client_reference_id', tid);
              link.setAttribute("href", url.toString());
            }
          });
        }, 800);
      }
      if (window.attachEvent) {
        window.attachEvent("onload", initializeFPRPaymentLinks);
      } else {
        window.addEventListener("load", initializeFPRPaymentLinks, false);
      }
    `;

    document.head.appendChild(scriptFPRInit);
    document.head.appendChild(scriptFPR);
    document.head.appendChild(scriptFPRLinks);

    return () => {
      document.head.removeChild(scriptFPRInit);
      document.head.removeChild(scriptFPR);
      document.head.removeChild(scriptFPRLinks);
    };
  }, []);

  // Watch for user login state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setIsSubscribed(false);
        setLoading(false);
      } else {
        setUser(currentUser);

        // Delay the referral call to ensure FirstPromoter has loaded.
        setTimeout(() => {
          if (window.fpr) {
            window.fpr("referral", {
              email: currentUser.email,
            });
          }
        }, 500);

        try {
          const res = await axios.post('/api/check-subscription', {
            email: currentUser.email,
          });
          setIsSubscribed(!!res.data.isSubscribed);
        } catch (err) {
          console.error('[App.js] Subscription check failed:', err);
          setIsSubscribed(false);
        } finally {
          setLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // For debugging
  useEffect(() => {
    console.log('Folders state:', folders);
  }, [folders]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDropContent = async (folderId, content) => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      await axios.post('/moveContent', { folderId, contentId: content.id }, {
        headers: { Authorization: 'Bearer ' + idToken }
      });
      console.log('Content moved successfully:', content);
    } catch (error) {
      console.error('Error moving content:', error);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading...</div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  if (!isSubscribed) {
    return (
      <div className="subscription-prompt">
        <div className="subscription-card">
          <h2>Get Started with Your Free Trial</h2>
          <p className="subtitle">Experience all features—completely free for 7 days.</p>
          <p className="subtitle">No payment info needed.</p>
          <div className="buy-button-container">
            <stripe-buy-button
              buy-button-id="buy_btn_1R9yKDL79rMHSHkCDkSVWOhb"
              publishable-key="pk_live_51QRYyML79rMHSHkCZkpwnxgH8JRKf4LefdLTRuzyRbNeLkKjxHEmdnYvKE7D2Ir4007OXUq1MqxunaqfYVJd4K1000fpKQKegM"
            ></stripe-buy-button>
          </div>

          <button className="logout-button" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="app-container app-main">
        <Sidebar
          onDropContent={handleDropContent}
          onSelectFolder={setSelectedFolder}
          selectedFolder={selectedFolder}
          folders={folders}
          setFolders={setFolders}
        />

        <div className="content-section">
          <div className="user-info d-flex align-items-center mb-4">
            {user.photoURL && (
              <img
                src={user.photoURL}
                alt="Profile"
                className="mr-4"
                width="40"
                height="40"
                style={{ borderRadius: '50%' }}
              />
            )}
            <div className="welcome-text">
              <h5 className="mb-0" style={{ fontSize: '16px' }}>
                Welcome {user.displayName?.split(' ')[0] || 'User'}!
              </h5>
            </div>

            <div className="action-buttons ml-auto">
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={handleLogout}
              >
                Logout
              </button>
              <button
                className="btn btn-outline-danger"
                style={{ marginLeft: '5px' }}
                onClick={() =>
                  window.open(
                    'https://billing.stripe.com/p/login/aEU4h5eglgQAfZe288',
                    '_blank'
                  )
                }
              >
                Manage Subscription
              </button>
            </div>
          </div>

          <ChatInterface user={user} />
          <ContentManager
            selectedFolder={selectedFolder}
            folders={folders}
            setFolders={setFolders}
          />
          <PopupChatbot user={user} />
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
